import React from 'react';
import kebabCase from 'lodash/kebabCase';
import { SearchTypes } from '@Constants/CommonConstants';
import RxStructureService from '@Services/RxStructureService';
import UserDatabases from '@Services/UserDatabases';
import { ButtonWithIcon } from '@els/els-react--button';
import StructureOpenInDatabase from '@Components/Results/structure/StructureOpenInDatabase';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import cn from 'classnames';
import PropTypes from 'prop-types';
import store from '@Store';
import StructureOptionsItem from '@Components/Results/structure/constants';
import {
  SYNTHESIS_OPTION_NAMES,
  isSynthesizeAvailable as isSynthesizeAvailableFn,
  synthesize as synthesizeFn,
} from '@Components/Results/structure/structure-synthesis.component';
import AppStorage from '@Services/AppStorage';
import * as Results from '@Services/Results';
import * as FormatUtils from '@Services/FormatUtils';
import StructureService from '@Services/Structure/StructureService';
import Similarity from '@Services/Similarity';
import { showCopiedToastMessage } from '@Containers/ToastMessages/CopiedToastMessage/CopiedToastMessageAction';
import { canUseStructureAsFilter as canUseStructureAsFilterUtil } from '@Components/Results/structure/structure-utils';
import './StructureToolbarOptionsContent.custom.scss';

export default class StructureToolbarOptionsContent extends React.Component {
  constructor(props) {
    super(props);

    this.findSimilar = this.findSimilar.bind(this);
    this.viewDetails = this.viewDetails.bind(this);
    this.viewRelatedMarkush = this.viewRelatedMarkush.bind(this);
    this.copySmiles = this.copySmiles.bind(this);
  }

  getCopyStructureToQueryParams() {
    const { onClosed } = this.props;
    const imgObj = FormatUtils.getImageFromPathForZoom(get(this.props, 'structure.molImage.path'), null, true);

    onClosed();

    return {
      structure: get(this.props, 'structure.molImage.structure'),
      preview: get(imgObj, 'url'),
    };
  }

  getCopyReactionToQueryParams() {
    const { onClosed } = this.props;

    onClosed();

    return {
      structure: get(this.props, 'metadata.rxnFile'),
      preview: get(this.props, 'metadata.rxnPath'),
    };
  }

  getUseAsFilterParams() {
    const { onClosed } = this.props;
    const imgObj = FormatUtils.getImageFromPathForZoom(get(this.props, 'structure.molImage.path'), null, true);

    onClosed();

    return {
      preview: get(imgObj, 'url'),
      data: get(this.props, 'structure.molImage.structure'),
    };
  }

  getAllRelatedSubstances() {
    const structureId = get(this.props, 'structure.details.id');
    const databaseId = get(this.props, 'structure.databaseId');

    RxStructureService.viewAllRelatedSubstances(structureId, databaseId);
  }

  copySmiles(structure) {
    const { onClosed } = this.props;
    onClosed();
    return StructureService.convertToSmiles(structure)
      .then((smiles) => {
        navigator.clipboard.writeText(smiles.structure);
        store.dispatch(showCopiedToastMessage('The SMILES string of the structure has been copied to the clipboard.'));
      })
      .catch(Logger.error);
  }

  findSimilar(molStructure) {
    const { onClosed, structure } = this.props;

    onClosed();

    return Similarity.showModalForStructure(structure, molStructure, get(this.props, 'structure.databaseId'))
      .then((modalResult) => AppStorage.instance.set('historyOfVisitedDbSimilarity', [modalResult.selectedResult]))
      .catch(Logger.error);
  }

  viewRelatedMarkush(markushRef) {
    const {
      onClosed,
      structure: {
        details: { id: structureId },
        databaseId,
      },
    } = this.props;

    onClosed();

    return Results.getMarkushStructures(structureId, databaseId, markushRef)
      .then((data) => RxStructureService.goTo(data, null, SearchTypes.MARKUSH_SEARCH))
      .catch(Logger.error);
  }

  viewDetails() {
    const {
      metadata: { id: query },
      structure: {
        details: { id: structureId },
        databaseId,
      },
    } = this.props;

    RxStructureService.viewDetails(query, structureId, databaseId);
  }

  doSynthesize(option, forPlanner = true) {
    const { onClosed, structure, spOptions, onAutoSynthesizeOptionClick } = this.props;

    onClosed();

    // AA
    if (option.name === 'auto') {
      onAutoSynthesizeOptionClick();
    }

    return synthesizeFn(structure, option, forPlanner, spOptions);
  }

  isAllowedOptionsItem(optionsItem) {
    const { allowedOptionsItems } = this.props;
    return allowedOptionsItems && allowedOptionsItems.includes(optionsItem);
  }

  // eslint-disable-next-line
  // TODO: Check if it is possible to exclude this method from the class, and use it as an outer function.
  // eslint-disable-next-line class-methods-use-this
  isSynthesisOptionDisabled(option, structure) {
    const { name } = option;
    const { molImage, databaseId, details } = structure || {};
    switch (name) {
      case SYNTHESIS_OPTION_NAMES.auto:
        return !(molImage && molImage.structure);
      case SYNTHESIS_OPTION_NAMES.manual:
        return isNil(details) || isNil(databaseId) || !UserDatabases.isDefaultDatabase(databaseId);
      default:
        return false;
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  render() {
    const { structure, metadata, synthesisOptions, className, onItemClick, trackingContext, onOpenInDatabaseOptionClick } = this.props;
    const {
      subLinks,
      details: { markushRef, markushStr, mixtureItems: mixture, id: substanceId } = {},
      molImage: { path, structure: molImageStructure },
    } = structure;
    const { context, isViewPreview } = metadata;
    const isSubstances = context === 'substances';
    const isReactions = context === 'reactions';
    const canUseStructureAsFilter = canUseStructureAsFilterUtil({ structure, metadata });
    const isMolImagePropertyPresent = !!path;
    const isStructuredSubstance = !!molImageStructure;
    const substanceHasId = !!substanceId;
    const isSynthesizeAvailable = synthesisOptions && isSynthesizeAvailableFn(structure);

    return (
      <ul role="menu" className={cn('els-btn-group-vertical', className)}>
        {this.isAllowedOptionsItem(StructureOptionsItem.FIND_SIMILAR) &&
          (mixture || !isMolImagePropertyPresent || !isStructuredSubstance || !substanceHasId ? null : (
            <li role="presentation">
              <ButtonWithIcon
                sprite="ChevronRight"
                type="link"
                linkIconSize="xs"
                role="menuitem"
                className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', 'e2e-find-similar')}
                onClick={() => {
                  onItemClick(StructureOptionsItem.FIND_SIMILAR);
                  this.findSimilar();
                }}>
                Find Similar
              </ButtonWithIcon>
            </li>
          ))}
        {this.isAllowedOptionsItem(StructureOptionsItem.VIEW_RELATED_MARKUSH) &&
          (!mixture && markushRef > 0 ? (
            <li role="presentation">
              <ButtonWithIcon
                sprite="ChevronRight"
                type="link"
                linkIconSize="xs"
                role="menuitem"
                className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', 'e2e-view-related-markush')}
                onClick={() => {
                  onItemClick(StructureOptionsItem.VIEW_RELATED_MARKUSH);
                  this.viewRelatedMarkush(markushRef);
                }}>
                View related Markush
              </ButtonWithIcon>
            </li>
          ) : null)}
        {this.isAllowedOptionsItem(StructureOptionsItem.VIEW_DETAILS) &&
          (substanceHasId && (isViewPreview || !isSubstances) ? (
            <li role="presentation">
              <ButtonWithIcon
                sprite="ChevronRight"
                type="link"
                linkIconSize="xs"
                role="menuitem"
                className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', 'e2e-view-details')}
                onClick={() => {
                  onItemClick(StructureOptionsItem.VIEW_DETAILS);
                  this.viewDetails();
                }}
                data-click-delegation
                data-tracking="true"
                data-tracking-context={trackingContext}
                data-tracking-type="structurecontentinteraction"
                data-tracking-action="option:view details">
                View details
              </ButtonWithIcon>
            </li>
          ) : null)}
        {this.isAllowedOptionsItem(StructureOptionsItem.SYNTHESIS_OPTIONS) && isSynthesizeAvailable && (
          <>
            <hr className="c-els-divider u-els-margin-top-1o4 u-els-margin-bottom-1o4" />

            <li role="presentation">
              <div className="synthesis-options-group">
                {synthesisOptions.map((option) => (
                  <ButtonWithIcon
                    key={option.name}
                    sprite="ChevronRight"
                    type="link"
                    linkIconSize="xs"
                    role="menuitem"
                    className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', `e2e-${kebabCase(option.title)}`)}
                    onClick={() => this.doSynthesize(option)}
                    disabled={this.isSynthesisOptionDisabled(option, structure)}>
                    {option.title}
                  </ButtonWithIcon>
                ))}
              </div>
            </li>
          </>
        )}
        {mixture ||
        !isMolImagePropertyPresent ||
        !(isStructuredSubstance || isReactions || canUseStructureAsFilter || markushStr) ? null : (
          <>
            <hr className="c-els-divider u-els-margin-top-1o4 u-els-margin-bottom-1o4" />
            {this.isAllowedOptionsItem(StructureOptionsItem.COPY_STRUCTURE_TO_QUERY) &&
              (!isStructuredSubstance ? null : (
                <li role="presentation">
                  <ButtonWithIcon
                    sprite="ChevronRight"
                    type="link"
                    linkIconSize="xs"
                    role="menuitem"
                    className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', 'e2e-copy-structure-to-query')}
                    onClick={() => {
                      onItemClick(StructureOptionsItem.COPY_STRUCTURE_TO_QUERY);
                      RxStructureService.copyToQuery(this.getCopyStructureToQueryParams());
                    }}>
                    Copy structure to query
                  </ButtonWithIcon>
                </li>
              ))}
            {this.isAllowedOptionsItem(StructureOptionsItem.COPY_REACTION_TO_QUERY) &&
              (isReactions ? (
                <li role="presentation">
                  <ButtonWithIcon
                    sprite="ChevronRight"
                    type="link"
                    linkIconSize="xs"
                    role="menuitem"
                    className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', 'e2e-copy-reaction-to-query')}
                    onClick={() => {
                      onItemClick(StructureOptionsItem.COPY_REACTION_TO_QUERY);
                      RxStructureService.copyToQuery(this.getCopyReactionToQueryParams());
                    }}
                    data-click-delegation
                    data-tracking="true"
                    data-tracking-context={trackingContext}
                    data-tracking-type="structurecontentinteraction"
                    data-tracking-action="option:copy reaction to query">
                    Copy reaction to query
                  </ButtonWithIcon>
                </li>
              ) : null)}
            {this.isAllowedOptionsItem(StructureOptionsItem.COPY_SMILES) &&
              (isStructuredSubstance ? (
                <li role="presentation">
                  <ButtonWithIcon
                    sprite="ChevronRight"
                    type="link"
                    linkIconSize="xs"
                    role="menuitem"
                    className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', 'e2e-copy-smiles')}
                    onClick={() => {
                      onItemClick(StructureOptionsItem.COPY_SMILES);
                      this.copySmiles(molImageStructure);
                    }}
                    data-click-delegation
                    data-tracking="true"
                    data-tracking-context={trackingContext}
                    data-tracking-type="structurecontentinteraction"
                    data-tracking-action="option:copy smiles">
                    Copy SMILES
                  </ButtonWithIcon>
                </li>
              ) : null)}
            {this.isAllowedOptionsItem(StructureOptionsItem.USE_AS_FILTER) &&
              (canUseStructureAsFilter ? (
                <li role="presentation">
                  <ButtonWithIcon
                    sprite="ChevronRight"
                    type="link"
                    linkIconSize="xs"
                    role="menuitem"
                    className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', 'e2e-use-as-filter')}
                    onClick={() => {
                      onItemClick(StructureOptionsItem.USE_AS_FILTER);
                      RxStructureService.useAsFilter(this.getUseAsFilterParams());
                    }}
                    data-click-delegation
                    data-tracking="true"
                    data-tracking-context={trackingContext}
                    data-tracking-type="structurecontentinteraction"
                    data-tracking-action="option:use as filter">
                    Use as filter
                  </ButtonWithIcon>
                </li>
              ) : null)}
            {this.isAllowedOptionsItem(StructureOptionsItem.GET_ALL_RELATED_SUBSTANCES) &&
              (markushStr ? (
                <li role="presentation">
                  <ButtonWithIcon
                    sprite="ChevronRight"
                    type="link"
                    linkIconSize="xs"
                    role="menuitem"
                    className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', 'e2e-get-all-related-substances')}
                    onClick={() => {
                      onItemClick(StructureOptionsItem.GET_ALL_RELATED_SUBSTANCES);
                      this.getAllRelatedSubstances();
                    }}
                    data-click-delegation
                    data-tracking="true"
                    data-tracking-context={trackingContext}
                    data-tracking-type="structurecontentinteraction"
                    data-tracking-action="option:get all related Substances">
                    Get all related Substances
                  </ButtonWithIcon>
                </li>
              ) : null)}
          </>
        )}

        {this.isAllowedOptionsItem(StructureOptionsItem.OPEN_IN_DATABASE) &&
          (!mixture && subLinks ? (
            <li role="presentation">
              <StructureOpenInDatabase metadata={metadata} subLinks={subLinks} onOptionClick={onOpenInDatabaseOptionClick} />
            </li>
          ) : null)}
      </ul>
    );
  }
}

StructureToolbarOptionsContent.defaultProps = {
  allowedOptionsItems: StructureOptionsItem.all,
  onClosed: () => null,
  onItemClick: () => null,
  onOpenInDatabaseOptionClick: () => null,
  onAutoSynthesizeOptionClick: () => null,
};

StructureToolbarOptionsContent.propTypes = {
  allowedOptionsItems: PropTypes.array, // allowed options items for context menu
  synthesisOptions: PropTypes.array,
  context: PropTypes.object,
  metadata: PropTypes.object,
  // eslint-disable-next-line react/no-unused-prop-types
  onToggleMenu: PropTypes.func,
  onClosed: PropTypes.func,
  onItemClick: PropTypes.func,
  onOpenInDatabaseOptionClick: PropTypes.func,
  onAutoSynthesizeOptionClick: PropTypes.func,
  structure: PropTypes.object,
  spOptions: PropTypes.object,
  className: PropTypes.string,
  trackingContext: PropTypes.string,
};
